import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Package,
  MessageSquare,
  ShoppingCart,
  Bookmark,
  Building,
  Loader2,
  Building2,
  MapPin,
  PhoneCall,
  MailIcon,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";

import {
  getCompanyWithParts,
  fetchSavedParts,
  removeSavedPart,
  savePart,
} from "../store/parts/partsAction";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead,
} from "../store/partChat/partChatActions";
import { openAuthDialog } from "../store/auth/authSlice";

import { useTheme } from "../components/ThemeProvider";
import PartImageSlider from "../hooks/partImageSlider";
import PartChatDialog from "@/components/PartChatDialog";
import PartPaymentModal from "@/components/Modal/PartPaymentModal";
import { getUserSubscription } from "../store/auth/authActions";

const VendorPublicPage = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const { currentSubscription, isUserSubscriptionLoading } = useSelector(
    (state) => state.auth,
  );
  const { vendorCompany, vendorParts, vendorLoading, savedParts } = useSelector(
    (state) => state.parts,
  );
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { messages, isMessagesLoading, isSenderLoading } = useSelector(
    (state) => state.partChat,
  );

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);

  useEffect(() => {
    if (slug) {
      dispatch(getCompanyWithParts(slug));
    }
    if (user && isAuthenticated) {
      dispatch(fetchSavedParts({ userId: user?.uid }));
    }
  }, [slug, user, isAuthenticated, dispatch]);

  useEffect(() => {
    if (vendorCompany?.id || vendorCompany?.uid) {
      dispatch(getUserSubscription(vendorCompany.id || vendorCompany.uid));
    }
  }, [vendorCompany, dispatch]);

  const handleChatClick = async (part, e) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to chat with this seller.",
        variant: "destructive",
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
        }),
      );
      return;
    }

    try {
      const sellerData = await dispatch(
        fetchSellerById({ userId: part?.sellerId }),
      ).unwrap();

      dispatch(
        fetchMessages({
          buyerId: user?.uid,
          sellerId: part?.sellerId,
          partId: part?.id,
          currentUserId: user?.uid,
        }),
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: user?.uid,
            receiverId: part?.sellerId,
            partId: part?.id,
          }),
        );
      }, 800);

      setSelectedConversation({
        partInfo: part,
        otherUserDetail: sellerData,
      });
    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Error",
        description: "Failed to open chat. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveClick = (part, e) => {
    e.stopPropagation();
    if (!isAuthenticated || !user) {
      toast({
        title: "Login Required",
        description: "Please login to save parts.",
        variant: "destructive",
      });
      return;
    }

    const isSaved = savedParts?.some(
      (saved) => saved.partId === part?.id && saved.userId === user?.uid,
    );

    if (isSaved) {
      dispatch(removeSavedPart({ userId: user?.uid, partId: part?.id }));
      toast({
        title: "Removed from saved",
        description: "Part removed from your saved list.",
      });
    } else {
      dispatch(savePart({ userId: user?.uid, part }));
      toast({
        title: "Saved successfully",
        description: "Part added to your saved list.",
      });
    }
  };

  const handleBuyClick = (part, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login as buyer to buy parts.",
        variant: "destructive",
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
        }),
      );
      return;
    }

    if (!part?.stripePaymentLink) {
      toast({
        title: "Payment Unavailable",
        description: "Seller hasn't added a payment link.",
        variant: "destructive",
      });
      return;
    }

    setSelectedPart(part);
    setShowPaymentModal(true);
  };

  const isDark = theme === "dark" || theme === "system";
  const borderColor = isDark ? "border-gray-800" : "border-gray-200";
  const textPrimary = isDark ? "text-gray-100" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-600";
  const textColor = isDark ? "text-gray-100" : "text-gray-900";

  if (vendorLoading || isUserSubscriptionLoading) {
    return (
      <div
        className={`flex justify-center items-center min-h-[70vh] bg-transparent`}
      >
        <Card className={`border ${borderColor} shadow-2xl rounded-2xl p-8`}>
          <CardContent className="flex flex-col items-center gap-6 p-8">
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
            <p className={`${textPrimary} text-center font-semibold text-lg`}>
              Loading vendor information...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!vendorCompany) {
    return (
      <div
        className={`flex justify-center items-center min-h-[80vh] px-6 py-12 bg-transparent `}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`w-full max-w-md`}
        >
          <Card
            className={`border ${borderColor} shadow-xl rounded-3xl overflow-hidden`}
          >
            <CardContent className="flex flex-col items-center text-center gap-6 p-8 md:p-10">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center shadow-inner">
                <Building2 className={`w-12 h-12 text-primary`} />
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Company Not Found
              </h2>

              <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed">
                We couldn't find this company's profile. It might have been
                moved, renamed, or doesn't exist anymore.
              </p>

              <p className="text-gray-500 dark:text-gray-500 text-sm italic">
                Try refreshing the page or returning to the homepage.
              </p>

              <div className="flex gap-3 mt-4">
                <Button
                  variant="default"
                  className="px-6 py-2 font-semibold"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  className={`${borderColor} px-6 py-2 font-semibold`}
                  onClick={() => navigate("/")}
                >
                  Go Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300`}>
      <div
        className={`relative py-12 px-6 mb-16 rounded-3xl border shadow-xl transition-all duration-500 transform hover:scale-[1.01] ${
          isDark
            ? "border-primary/20 bg-gradient-to-br from-[#0A0F1E] via-[#111C2A] to-[#1E2A38]"
            : "border-gray-200 bg-gradient-to-br from-white via-[#f9fafb] to-[#f0f3f8]"
        }`}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-72 h-72 bg-primary/15 blur-3xl animate-pulse-slow rounded-full" />
          <div className="absolute top-12 right-12 w-64 h-64 bg-blue-400/15 blur-3xl animate-pulse-slow rounded-full" />
        </div>

        <div
          className="relative z-10 max-w-6xl mx-auto flex flex-col lg:flex-row 
items-center lg:items-start text-center lg:text-left gap-6 px-4 w-full"
        >
          {vendorCompany?.logoUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
              className="relative mb-2"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-600 rounded-2xl blur opacity-30 animate-pulse"></div>
              <img
                src={vendorCompany.logoUrl}
                alt={`${vendorCompany.companyName} logo`}
                className="relative w-full xs:w-40 h-40 object-cover bg-white dark:bg-gray-900 rounded-2xl border-4 border-white dark:border-gray-800 shadow-xl"
              />
            </motion.div>
          )}
          {/* Left: Company Info */}
          <div className="flex-1 flex flex-col gap-4 w-full">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold py-2 text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-400 to-pink-400 animate-gradient-x leading-snug break-words">
              {vendorCompany?.companyName || "Company Name"}
            </h1>

            <div className="w-20 h-1 mx-auto lg:mx-0 rounded-full bg-gradient-to-r from-primary via-blue-400 to-pink-400 animate-gradient-x" />

            <p
              className={`text-sm md:text-base ${
                isDark ? "text-gray-300" : "text-gray-700"
              } max-w-md mx-auto lg:mx-0 break-words`}
            >
              {vendorCompany?.bio ||
                `${vendorCompany?.companyName} is a trusted supplier providing high-quality products with exceptional service.`}
            </p>
          </div>

          {/* Right: Contact Info */}
          <div className="flex flex-col gap-3 flex-shrink-0 w-full sm:w-96 lg:w-auto">
            {[
              { key: "email", icon: <MailIcon /> },
              { key: "phone", icon: <PhoneCall /> },
              { key: "postalCode", icon: <MapPin /> },
            ].map(({ key, icon }) => {
              if (!vendorCompany?.[key]) return null;

              const value = vendorCompany[key];
              const href =
                key === "email"
                  ? `mailto:${value}`
                  : key === "phone"
                    ? `tel:${value}`
                    : undefined;

              return (
                <div
                  key={key}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl w-full
          backdrop-blur-sm border transition-all duration-300 
          hover:shadow-lg ${
            isDark
              ? "bg-gradient-to-r from-primary/15 via-blue-500/10 to-pink-400/10 border-white/20 text-gray-200"
              : "bg-white border-gray-200 text-gray-700 shadow-sm"
          }`}
                >
                  <span className="text-base">{icon}</span>

                  {href ? (
                    <a
                      href={href}
                      className="text-sm md:text-base font-medium hover:text-primary 
              transition-colors truncate w-full text-left"
                    >
                      {value}
                    </a>
                  ) : (
                    <span className="text-sm md:text-base font-medium truncate w-full text-left">
                      {value}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16 space-y-8">
        {vendorParts && vendorParts.length > 0 ? (
          <Card
            className={`bg-transparent border ${borderColor} shadow-lg rounded-xl overflow-hidden`}
          >
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col sm:flex-row items-center justify-between mb-8 text-center sm:text-left gap-4 sm:gap-0">
                {/* Title + Icon */}
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3">
                  <Package className="text-primary w-10 h-10 sm:w-7 sm:h-7 mx-auto sm:mx-0" />

                  <h2
                    className={`text-xl sm:text-2xl md:text-3xl font-bold ${textPrimary}`}
                  >
                    Available Parts
                  </h2>
                </div>

                {/* Count Badge */}
                <Badge
                  variant="secondary"
                  className="text-sm px-4 py-1.5 font-semibold mx-auto md:mx-0"
                >
                  {vendorParts.length}{" "}
                  {vendorParts.length === 1 ? "Part" : "Parts"}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {vendorParts.map((part, index) => {
                  const isSaved = savedParts?.some(
                    (saved) =>
                      saved.partId === part?.id && saved.userId === user?.uid,
                  );

                  return (
                    <motion.div
                      key={part.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => navigate(`/partDetail/${part?.id}`)}
                      className={`group relative flex flex-col justify-between overflow-hidden rounded-2xl border cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:shadow-xl ${
                        isDark
                          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 border-gray-800 hover:border-primary/60"
                          : "bg-gradient-to-br from-white via-gray-50 to-gray-100 border-gray-200 hover:border-primary/30"
                      }`}
                    >
                      <div className="relative h-52 w-full overflow-hidden">
                        <PartImageSlider
                          images={
                            part?.imageUrls?.length
                              ? part.imageUrls
                              : part?.imageReferences?.length
                                ? part.imageReferences
                                : part?.imageUrl
                                  ? [part.imageUrl]
                                  : []
                          }
                          enableSlider={false}
                          alt={`Image of ${part?.name}`}
                        />

                        {user?.role !== "seller" && (
                          <button
                            onClick={(e) => handleSaveClick(part, e)}
                            className="absolute top-3 right-3 p-2 rounded-full backdrop-blur-md bg-white/70 dark:bg-gray-800/70 hover:bg-primary/20 transition-all duration-300"
                          >
                            {isSaved ? (
                              <Bookmark className="w-5 h-5 text-primary fill-primary" />
                            ) : (
                              <Bookmark className="w-5 h-5 text-gray-500 group-hover:text-primary" />
                            )}
                          </button>
                        )}
                        {part?.isFounderMember && (
                          <span
                            className={`absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] text-white font-semibold border border-yellow-100 bg-yellow-600`}
                          >
                            Founding Seller
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col flex-1 justify-between p-5">
                        <div className="space-y-3 flex-1">
                          <div className="flex flex-col xs:flex-row items-center xs:justify-between mb-4 text-center xs:text-left gap-4">
                            <div className="mb-2 flex items-baseline space-x-1">
                              <span className="text-lg font-semibold text-primary">
                                $
                              </span>
                              <span
                                className={`text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500`}
                              >
                                {part?.price
                                  ? part.price.toLocaleString()
                                  : "N/A"}
                              </span>
                            </div>
                            {part.status && (
                              <Badge
                                variant="secondary"
                                className={`text-xs px-3 py-1 rounded-full cursor-default transition-none
      ${
        part.status?.toLowerCase().includes("low")
          ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800"
          : "bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800"
      }`}
                              >
                                {part.status}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <h3
                              className={`text-lg font-semibold line-clamp-2 ${
                                isDark ? "text-gray-100" : "text-gray-900"
                              }`}
                              title={`${part.make} ${part.model} ${part.year} ${part.name}`}
                            >
                              {[
                                part.make?.toLowerCase() === "other"
                                  ? part.sub_make
                                  : part.make,
                                part.model?.toLowerCase() === "other"
                                  ? part.sub_model
                                  : part.model,
                                part.year,
                                part.name?.toLowerCase() === "other"
                                  ? part.sub_name
                                  : part.name,
                              ]
                                .filter(Boolean)
                                .map((p) => String(p).trim())
                                .map(
                                  (p) => p.charAt(0).toUpperCase() + p.slice(1),
                                )
                                .join(" ")}
                            </h3>
                          </div>

                          <p
                            className={`text-sm line-clamp-2 ${
                              isDark ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {part.description ||
                              `High quality ${
                                part?.name == "other"
                                  ? part?.sub_name
                                  : part?.name || "part"
                              }.`}
                          </p>

                          <div className="flex items-center text-xs mt-3">
                            {part.sellerLogo || vendorCompany?.logoUrl ? (
                              <img
                                src={part.sellerLogo || vendorCompany?.logoUrl}
                                alt="seller logo"
                                className="w-5 h-5 rounded-sm mr-2 object-contain"
                              />
                            ) : (
                              <Building className="w-4 h-4 mr-2 text-muted-foreground" />
                            )}
                            <span
                              className={
                                isDark ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              {part.sellerName || vendorCompany?.companyName}
                            </span>
                          </div>
                        </div>

                        {user?.role === "seller" &&
                        user?.uid === part?.sellerId ? null : (
                          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={(e) => handleChatClick(part, e)}
                              >
                                <MessageSquare className="w-4 h-4 mr-1" />
                                Chat
                              </Button>
                              {part?.stripePaymentLink &&
                                part?.stripePaymentLink !== "" &&
                                user?.role !== "seller" && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className="w-full group bg-primary text-white hover:bg-primary/70"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBuyClick(part, e);
                                    }}
                                  >
                                    <ShoppingCart className="w-4 h-4 mr-1" />
                                    Buy
                                  </Button>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card
            className={`bg-transparent border ${borderColor} shadow-lg rounded-xl overflow-hidden`}
          >
            <CardContent className="p-12 text-center">
              <div
                className={`w-16 h-16 rounded-full ${
                  isDark ? "bg-gray-800" : "bg-gray-100"
                } flex items-center justify-center mx-auto mb-4`}
              >
                <Package className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${textPrimary}`}>
                No Parts Available
              </h3>
              <p className={`${textSecondary}`}>
                This vendor hasn't listed any parts yet. Check back soon!
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => !open && setSelectedConversation(null)}
        />
      )}

      {showPaymentModal && selectedPart && (
        <PartPaymentModal
          open={showPaymentModal}
          part={selectedPart}
          close={() => setShowPaymentModal(false)}
        />
      )}
    </div>
  );
};

export default VendorPublicPage;
